<template>
  <div class="cash-call-form-submit1">
    <BaseModal v-model="internalVisible" :title="title" hide-footer>
      <div class="p-1">
        <label style='font-family:"Segoe UI Regular", sans-serif;font-weight: bold;'>
          Please select the recipient(s) of the Cash Call:
        </label>

        <!-- RECIPIENTS LABEL -->
        <label class="recipients-label mt-2">Recipients :</label>

        <!-- SELECT RECIPIENTS TOGGLE -->
        <div class="d-flex align-items-center">
          <b-button
            class="select-recipients-toggle"
            @click="recipientsExpanded = !recipientsExpanded"
            :aria-expanded="recipientsExpanded ? 'true' : 'false'"
          >
            SELECT RECIPIENTS
            <span class="toggle-caret ml-2">{{ recipientsExpanded ? '▲' : '▼' }}</span>
          </b-button>
        </div>

        <b-collapse v-model="recipientsExpanded" class="mt-2">
          <!-- GLOBAL SEARCH -->
          <b-form-input
            v-model="recipientSearch"
            placeholder="Search email..."
            class="recipient-global-search mb-2"
          />

          <!-- ============================================================
               CO-LEAD: one group PER UMR, each containing its 4 parties.
               A new (non-LimeIQ) underwriter's UMR shows a support message
               instead of recipient lists / add buttons.
               ============================================================ -->
          <b-accordion v-if="isCoLead" id="recipients-accordion-colead">
            <b-card
              v-for="umr in umrGroups"
              :key="umr.key_id"
              no-body
              class="mb-2 border"
            >
              <b-card-header header-tag="header" class="recipient-umr-header p-0" role="tab">
                <div class="recipient-umr-trigger px-3 py-2" v-b-toggle="`umr-${umr.key_id}`">
                  <span class="umr-title">{{ umr.umr || umr.bdx_key }}</span>
                  <span class="umr-uw ml-2">— {{ umr.underwriter || '—' }}</span>
                </div>
              </b-card-header>

              <b-collapse :id="`umr-${umr.key_id}`" accordion="recipients-accordion-colead">
                <b-card-body class="py-2 px-3 border-0">
                  <!-- The 4 party sub-groups are ALWAYS shown. A brand-new
                       broker/underwriter company (added via Add Missing UMR) no
                       longer hides them: the user may add a recipient email for
                       that group with no domain limit; on send that recipient is
                       redirected to support@limeiq.com so LimeIQ can onboard the
                       company + user (see per-group message + backend override). -->
                  <template>
                    <div
                      v-for="party in partyConfig"
                      :key="party.key"
                      class="colead-party-block mb-1"
                    >
                      <div class="colead-party-header d-flex align-items-center justify-content-between px-2 py-1">
                        <div class="d-flex align-items-center flex-grow-1">
                          <input
                            type="checkbox"
                            class="group-select-all mr-2"
                            :checked="isKeyPartyAllSelected(umr.key_id, party.key)"
                            :indeterminate.prop="isKeyPartyIndeterminate(umr.key_id, party.key)"
                            @click.stop="toggleKeyPartyAll(umr.key_id, party.key, $event.target.checked)"
                          />
                          <span class="group-title">{{ party.label }}</span>
                          <span class="count-badge ml-2">
                            {{ selectedCountByKeyParty(umr.key_id, party.key) }}/{{ (umr.parties[party.key] || []).length }}
                          </span>
                        </div>
                        <b-button
                          class="add-new-btn"
                          v-b-tooltip.hover
                          title="Add new"
                          @click.stop="openAddRecipientColead(umr, party)"
                        >
                          +
                        </b-button>
                      </div>

                      <!-- One common message for ANY party with no vetted mailbox
                           on this UMR (a flagged brand-new broker/underwriter, or
                           any 0/0 party). The recipient can be added freely with
                           no domain limit, and on send is redirected to support
                           so LimeIQ can onboard the company + user. -->
                      <div
                        v-if="umrPartyNeedsSupport(umr, party.key)"
                        class="colead-new-uw-msg mx-2 my-1"
                      >
                        {{ party.label }} is not set up in LimeIQ. Add the {{ party.label.toLowerCase() }} recipient
                        here, on send it will be redirected to
                        <a href="mailto:support@limeiq.com">support@limeiq.com</a>
                        for LimeIQ to create a new user and forward the cash call.
                      </div>

                      <div class="colead-party-body px-2" style="max-height: 120px; overflow-y: auto;">
                        <b-form-checkbox
                          v-for="member in filteredKeyPartyEmails(umr, party.key)"
                          :key="member.email"
                          :checked="isRecipientSelected(umr.key_id, member.email)"
                          class="mb-1"
                          @change="toggleKeyRecipient(umr.key_id, member.email, $event)"
                        >
                          {{ member.email }}
                        </b-form-checkbox>
                        <div v-if="filteredKeyPartyEmails(umr, party.key).length === 0" class="text-muted small">
                          No results found.
                        </div>
                      </div>
                    </div>
                  </template>
                </b-card-body>
              </b-collapse>
            </b-card>
          </b-accordion>

          <!-- ============================================================
               SINGLE-KEY (normal cash call): flat 4 groups. Unchanged.
               ============================================================ -->
          <b-accordion v-else id="recipients-accordion">
            <b-card
              v-for="group in orderedGroups"
              :key="group.key"
              no-body
              class="mb-1 border-0"
            >
              <b-card-header header-tag="header" class="recipient-accordion-header p-0" role="tab">
                <div class="recipient-accordion-trigger d-flex align-items-center justify-content-between px-3 py-2">
                  <div class="d-flex align-items-center flex-grow-1">
                    <input
                      type="checkbox"
                      class="group-select-all mr-2"
                      :checked="isGroupAllSelected(group.key)"
                      :indeterminate.prop="isGroupIndeterminate(group.key)"
                      @click.stop="toggleGroupAll(group.key, $event.target.checked)"
                    />
                    <div
                      class="d-flex align-items-center flex-grow-1 group-toggle-area"
                      v-b-toggle="`acc-${group.key}`"
                    >
                      <span class="group-title">{{ group.label }}</span>
                      <span class="count-badge ml-2">
                        {{ selectedCountByGroup(group.key) }}/{{ (people[group.key] || []).length }}
                      </span>
                    </div>
                  </div>
                  <div class="d-flex align-items-center">
                    <b-button
                      class="add-new-btn"
                      v-b-tooltip.hover
                      title="Add new"
                      @click.stop="openAddRecipient(group)"
                    >
                      +
                    </b-button>
                    <i
                      class="chevron ml-2"
                      :class="{ rotated: activeGroup === group.key }"
                      v-b-toggle="`acc-${group.key}`"
                    ></i>
                  </div>
                </div>
              </b-card-header>

              <b-collapse
                :id="`acc-${group.key}`"
                accordion="recipients-accordion"
                @show="activeGroup = group.key"
                @hide="activeGroup = activeGroup === group.key ? null : activeGroup"
              >
                <b-card-body class="py-2 px-3 border-0">
                  <!-- Same notice as the co-lead flow: a party with no contacts
                       in LimeIQ has no vetted mailbox, so a recipient added here
                       is redirected to support on send. -->
                  <div
                    v-if="groupNeedsSupport(group.key)"
                    class="colead-new-uw-msg mb-2"
                  >
                    {{ group.label }} is not set up in LimeIQ. Add the recipient
                    here, on send it will be redirected to
                    <a href="mailto:support@limeiq.com">support@limeiq.com</a>
                    for LimeIQ to create a new user and forward the cash call.
                  </div>
                  <div style="max-height: 150px; overflow-y: auto;">
                    <b-form-checkbox
                      v-for="member in filteredGroupEmails(group.key)"
                      :key="member.email"
                      v-model="recipients"
                      :value="member.email"
                      class="mb-1"
                    >
                      {{ member.email }}
                    </b-form-checkbox>
                    <div v-if="filteredGroupEmails(group.key).length === 0" class="text-muted small">
                      No results found.
                    </div>
                  </div>
                </b-card-body>
              </b-collapse>
            </b-card>
          </b-accordion>
        </b-collapse>

        <div v-if="loading" class="text-muted small">Loading recipients...</div>

        <!-- SELECTED RECIPIENTS SUMMARY (single-key) -->
        <div v-if="!isCoLead && recipients.length" class="mt-3">
          <label class="font-weight-bold">Selected recipients:</label>
          <div class="d-flex flex-wrap">
            <span
              v-for="email in recipients"
              :key="email"
              class="badge badge-info mr-2 mb-2 d-flex align-items-center"
            >
              {{ email }}
              <span class="ml-2 cursor-pointer" @click="removeRecipient(email)">✕</span>
            </span>
          </div>
        </div>

        <!-- SELECTED RECIPIENTS SUMMARY (co-lead: grouped per UMR) -->
        <div v-if="isCoLead && totalSelectedCount" class="mt-3">
          <label class="font-weight-bold">Selected recipients:</label>
          <div v-for="umr in umrGroups" :key="`sel-${umr.key_id}`">
            <div v-if="(keyRecipients[umr.key_id] || []).length" class="mb-2">
              <div class="small text-muted">{{ umr.umr || umr.bdx_key }} — {{ umr.underwriter }}</div>
              <div class="d-flex flex-wrap">
                <span
                  v-for="email in keyRecipients[umr.key_id]"
                  :key="`${umr.key_id}-${email}`"
                  class="badge badge-info mr-2 mb-2 d-flex align-items-center"
                >
                  {{ email }}
                  <span class="ml-2 cursor-pointer" @click="toggleKeyRecipient(umr.key_id, email, false)">✕</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- URGENCY OPTIONS: independent toggles, default off. Shown for both
           normal and co-lead sends.
           - High Importance flags the outgoing email as high importance.
           - Urgent prepends "URGENT!" to the email subject line. -->
      <div class="cc-send-urgency">
        <div class="cc-send-urgency__row">
          <span class="cc-send-urgency__label">
            Mark as High Importance <span class="text-danger">!</span>
            <b-icon icon="info-circle" v-b-tooltip.hover="'Flags the outgoing email as high importance.'" />
          </span>
          <BaseSwitch v-model="markHighImportance" />
        </div>
        <div class="cc-send-urgency__row">
          <span class="cc-send-urgency__label">
            URGENT!
            <b-icon icon="info-circle" v-b-tooltip.hover="'Prepends &quot;URGENT!&quot; to the email subject line.'" />
          </span>
          <BaseSwitch v-model="markUrgent" />
        </div>
      </div>

      <!-- FOOTER -->
      <div style="float:right; margin-top:15px">
        <b-button variant="secondary" @click="closeModal">Cancel</b-button>
        <!-- conditional button -->
        <b-button
          v-if="cashCall && cashCall.internal_approval_required &&
          (cashCall.Status === 'DRAFT' || cashCall.Status === '' || cashCall.Status === 'SAVED' || cashCall.Status === 'WITHDRAWN')"
          variant="primary"
          @click="openTpaApprovalModal"
          :disabled="submitDisabled"
          class="ml-2"
        >
          Send for TPA Internal Approval
        </b-button>
        <b-button
          v-else
          variant="primary"
          @click="submitForm"
          :disabled="submitDisabled"
          class="ml-2"
        >
          Send Cash Call
        </b-button>
      </div>
    </BaseModal>

    <!-- ADD NEW RECIPIENT POPOUT -->
    <BaseModal
      v-model="addRecipientVisible"
      :title="`Add new recipient — ${addRecipientGroup ? addRecipientGroup.label : ''}`"
      hide-footer
      class="add-recipient-popout"
    >
      <div class="p-1">
        <label class="add-recipient-field-label">Email address</label>
        <div class="d-flex align-items-stretch add-recipient-input-row">
          <input
            type="text"
            class="form-control add-recipient-prefix"
            v-model="addRecipientPrefix"
            placeholder="Enter name or prefix"
            data-lpignore="true"
            autocomplete="off"
            @keyup.enter="confirmAddRecipient"
          />
          <span class="add-recipient-at">@</span>
          <input
            type="text"
            class="form-control add-recipient-domain"
            v-model="addRecipientDomain"
            :readonly="addRecipientDomainLocked"
            :class="{ 'is-locked': addRecipientDomainLocked }"
            placeholder="domain.com"
          />
        </div>

        <p class="add-recipient-helper">
          Domain is pre-filled based on the recipient group. To add an address
          from a different domain, use the main recipient selector.
        </p>

        <div v-if="addRecipientError" class="text-danger small mt-1">
          {{ addRecipientError }}
        </div>
      </div>

      <div style="float:right; margin-top:15px">
        <b-button variant="secondary" @click="addRecipientVisible = false">Cancel</b-button>
        <b-button
          variant="primary"
          class="ml-2"
          @click="confirmAddRecipient"
          :disabled="!canAddRecipient"
        >
          Add Recipient
        </b-button>
      </div>
    </BaseModal>

    <!-- TPA INTERNAL APPROVAL MODAL -->
    <BaseModal v-model="tpaApprovalVisible" title="Send for TPA Internal Approval" hide-footer>
      <div class="p-1">
        <label style='font-family:"Segoe UI Regular", sans-serif;font-weight: bold;'>
          Please select TPA approver(s):
        </label>

        <!-- ADD NEW APPROVER EMAIL -->
        <div class="mt-2 mb-2">
          <label style="font-family:'Segoe UI Regular', sans-serif;font-weight:bold;color:teal;">
            Add a new approver:
          </label>
          <div class="d-flex">
            <input
              type="email"
              class="form-control"
              v-model="manualApproverEmail"
              placeholder="Enter email address"
              data-lpignore="true"
              autocomplete="new-email"
            />
            <b-button
              class="ml-2"
              variant="primary"
              @click="addManualApprover"
              :disabled="!isValidApproverEmail"
            >
              Add
            </b-button>
          </div>
          <div v-if="!isValidApproverEmail && manualApproverEmail.length > 0" class="text-danger small">
            Please enter a valid email.
          </div>
          <div v-if="approverDomainError" class="text-danger small mt-1">
            {{ approverDomainError }}
          </div>
        </div>

        <!-- Search -->
        <b-form-input
          v-model="tpaMemberSearch"
          size="sm"
          placeholder="Search TPA member..."
          class="mb-2 mt-2"
        />

        <!-- Select All -->
        <div class="d-flex align-items-center mb-2 pb-1" style="border-bottom: 1px solid #eee;">
          <b-form-checkbox
            :checked="isTpaAllSelected"
            :indeterminate="isTpaIndeterminate"
            @change="toggleTpaAll"
          >
            <span class="text-muted" style="font-size:12px;">Select all</span>
          </b-form-checkbox>
        </div>

        <!-- TPA Member List -->
        <div style="max-height: 200px; overflow-y: auto;">
          <template v-if="tpaMembers.length === 0 && !tpaMemberSearch">
            <div class="text-muted small">
              No Cash Call Approvers assigned for this TPA.
            </div>
          </template>
          <template v-else>
            <b-form-checkbox
              v-for="member in filteredTpaMembers"
              :key="member.email"
              v-model="selectedTpaApprovers"
              :value="member.email"
              class="mb-1"
            >
              {{ member.name || member.email }}
              <span class="text-muted small">({{ member.email }})</span>
            </b-form-checkbox>
            <div v-if="filteredTpaMembers.length === 0 && tpaMemberSearch" class="text-muted small">
              No results found.
            </div>
          </template>
        </div>

        <!-- Selected Summary -->
        <div v-if="selectedTpaApprovers.length" class="mt-3">
          <label class="font-weight-bold">Selected approvers:</label>
          <div class="d-flex flex-wrap">
            <span
              v-for="email in selectedTpaApprovers"
              :key="email"
              class="badge badge-info mr-2 mb-2 d-flex align-items-center"
            >
              {{ email }}
              <span class="ml-2 cursor-pointer" @click="removeTpaApprover(email)">✕</span>
            </span>
          </div>
        </div>
      </div>

      <!-- FOOTER -->
      <div style="float:right; margin-top:15px">
        <b-button variant="secondary" @click="tpaApprovalVisible = false">Cancel</b-button>
        <b-button
          variant="primary"
          @click="submitTpaApproval"
          :disabled="!selectedTpaApprovers.length"
          class="ml-2"
        >
          Send for Approval
        </b-button>
      </div>
    </BaseModal>
  </div>
</template>

<script>
import { BaseModal, BaseSwitch } from '@components/base'
import CashCallService from '../../services/CashCallService'

export default {
  name: 'CashCallFormSubmit',
  components: { BaseModal, BaseSwitch },
  props: {
    value: Boolean,
    cashCall: { type: Object, default: null },
    // Modal title. Defaults to the normal cash call heading; the co-lead flow
    // passes "Send Co-Lead Cash Call".
    title: { type: String, default: 'Send Cash Call' },
    // When true, at least one Broker-group recipient must be selected before the
    // Cash Call can be sent (used by the co-lead flow).
    brokerRequired: { type: Boolean, default: false },
  },

  mounted() {
    document.body.classList.add('cash-call-form-submit')
  },
  beforeDestroy() {
    document.body.classList.remove('cash-call-form-submit')
  },
  
  data() {
    return {
      internalVisible: this.value,
      people: {},
      allowedEmailDomains: {},
      loading: false,
      recipients: [],
      activeGroup: null,

      // ---- Co-lead per-UMR recipients ----
      // isCoLead: set from the recipients endpoint response (co_lead flag).
      // umrGroups: [{ key_id, umr, bdx_key, underwriter, underwriter_limeiq_active, parties:{broker,Underwriter,TPA,coverholder} }]
      // keyRecipients: { [key_id]: [email, ...] } - selections tracked per UMR
      //   so the same person under two UMRs is a distinct selection and the
      //   send payload can target each UMR correctly.
      isCoLead: false,
      // Urgency toggles (co-lead), independent, default off:
      //  - markHighImportance: flag the outgoing email as high importance.
      //  - markUrgent: prepend "URGENT!" to the email subject line.
      markHighImportance: false,
      markUrgent: false,
      umrGroups: [],
      keyRecipients: {},
      // Per-UMR ORIGINAL per-party contact counts, snapshotted at load
      // (key_id -> { broker, Underwriter, TPA, coverholder }). Used to decide
      // "party with no vetted mailbox" so the domain stays blank/editable even
      // after any-domain recipients are hand-added, for ALL parties.
      originalContactCounts: {},
      // SINGLE-KEY: per-party ORIGINAL contact counts, snapshotted at load, so a
      // 0/0 group keeps its "not set up in LimeIQ" state after the user adds a
      // recipient to it.
      originalGroupContactCount: {},
      partyConfig: [
        { key: 'broker', label: 'Broker', domainRole: 'broker' },
        { key: 'Underwriter', label: 'Underwriter', domainRole: 'underwriter' },
        { key: 'TPA', label: 'TPA', domainRole: 'tpa' },
        { key: 'coverholder', label: 'Coverholder', domainRole: 'coverholder' },
      ],

      // recipient selector (mock layout)
      recipientsExpanded: false,
      recipientSearch: '',

      // Add new recipient popout
      addRecipientVisible: false,
      addRecipientGroup: null,   // { key, label, domainRole }
      addRecipientPrefix: '',
      addRecipientDomain: '',
      addRecipientDomainLocked: false,
      addRecipientError: '',

      // TPA approval modal
      tpaApprovalVisible: false,
      tpaMembers: [],
      selectedTpaApprovers: [],
      tpaMemberSearch: '',
      manualApproverEmail: '',
      approverDomainError: '',
    }
  },
  watch: {
    value(val) {
      this.internalVisible = val
      this.resetFormState()
      if (val) {
        this.fetchRecipients()
        this.fetchAllowedEmailDomains()
      }
    },
    internalVisible(val) {
      this.$emit('input', val)
    },
  },
  computed: {
    // Fixed display order + labels from the mock. `domainRole` is the key used
    // by the allowed-email-domains endpoint to pre-fill the "Add new" popout.
    orderedGroups() {
      const config = [
        { key: 'broker', label: 'Broker', domainRole: 'broker' },
        { key: 'Underwriter', label: 'MA', domainRole: 'underwriter' },
        { key: 'TPA', label: 'TPA', domainRole: 'tpa' },
        { key: 'coverholder', label: 'Coverholder', domainRole: 'coverholder' },
      ]
      // Only show groups the API actually returned, preserving the mock order.
      let groups = config.filter(g => Object.prototype.hasOwnProperty.call(this.people, g.key))
      // While searching, collapse (hide) groups that have no matching emails.
      if ((this.recipientSearch || '').trim()) {
        groups = groups.filter(g => this.filteredGroupEmails(g.key).length > 0)
      }
      return groups
    },

    allowedDomain() {
      // flatten all domains for recipient validation
      return Object.values(this.allowedEmailDomains).flat()
    },
    // At least one recipient from the Broker group is selected.
    hasBrokerSelected() {
      if (this.isCoLead) {
        // Any UMR's Broker party with a selected recipient satisfies it.
        return this.umrGroups.some(umr => {
          const brokers = (umr.parties && umr.parties.broker) || []
          const selected = this.keyRecipients[umr.key_id] || []
          return brokers.some(m => selected.includes(m.email))
        })
      }
      const brokers = this.people.broker || []
      return brokers.some(m => this.recipients.includes(m.email))
    },
    // Total recipients selected (co-lead: across all UMRs; single-key: flat).
    totalSelectedCount() {
      if (this.isCoLead) {
        return Object.values(this.keyRecipients)
          .reduce((n, list) => n + ((list || []).length), 0)
      }
      return this.recipients.length
    },
    submitDisabled() {
      // Per-UMR blocking (not global): an un-sendable UMR (brand-new
      // underwriter, or a manual UMR with a brand-new broker) simply gets no
      // recipients and is skipped - the OTHER UMRs still send. So we no longer
      // disable the whole Send for such a UMR.
      if (!this.totalSelectedCount) return true
      // Broker recipient is no longer mandatory - a co-lead can be sent without
      // a broker recipient selected. (Send only requires >=1 recipient overall.)
      return false
    },

    canAddRecipient() {
      const prefix = (this.addRecipientPrefix || '').trim()
      const domain = (this.addRecipientDomain || '').trim()
      if (!prefix || !domain) return false
      return this.isValidEmail1(`${prefix}@${domain}`)
    },

    allowedTpaDomains() {
      // only TPA domains for approver validation
      return this.allowedEmailDomains['tpa'] || []
    },

    isValidApproverEmail() {
      if (!this.manualApproverEmail) return false
      return /^\S+@\S+\.\S+$/.test(this.manualApproverEmail)
    },

    // TPA approval computed
    filteredTpaMembers() {
      const search = this.tpaMemberSearch.toLowerCase()
      if (!search) return this.tpaMembers
      return this.tpaMembers.filter(m =>
        (m.name && m.name.toLowerCase().includes(search)) ||
        (m.email && m.email.toLowerCase().includes(search))
      )
    },
    isTpaAllSelected() {
      return (
        this.filteredTpaMembers.length > 0 &&
        this.filteredTpaMembers.every(m => this.selectedTpaApprovers.includes(m.email))
      )
    },
    isTpaIndeterminate() {
      const count = this.filteredTpaMembers.filter(m =>
        this.selectedTpaApprovers.includes(m.email)
      ).length
      return count > 0 && count < this.filteredTpaMembers.length
    },
  },
  methods: {
    filteredGroupEmails(group) {
      var members = this.people[group] || []
      var search = (this.recipientSearch || '').toLowerCase().trim()
      if (!search) return members
      return members.filter(function(m) {
        return (
          m.email.toLowerCase().indexOf(search) !== -1
        )
      })
    },

    selectedCountByGroup(group) {
      var members = this.people[group] || []
      return members.filter(m => this.recipients.includes(m.email)).length
    },

    isGroupAllSelected(group) {
      var members = this.people[group] || []
      return members.length > 0 && members.every(m => this.recipients.includes(m.email))
    },

    isGroupIndeterminate(group) {
      var count = this.selectedCountByGroup(group)
      var total = (this.people[group] || []).length
      return count > 0 && count < total
    },

    toggleGroupAll(group, checked) {
      var members = this.people[group] || []
      if (checked) {
        members.forEach(m => {
          if (!this.recipients.includes(m.email)) {
            this.recipients.push(m.email)
          }
        })
      } else {
        const groupEmails = members.map(m => m.email)
        this.recipients = this.recipients.filter(e => !groupEmails.includes(e))
      }
    },

    // -------------------------------------------------------------------
    // CO-LEAD per-UMR recipient helpers. Selections live in keyRecipients
    // keyed by key_id, so the same email under two UMRs is two independent
    // selections and the send payload targets each UMR correctly.
    // -------------------------------------------------------------------
    filteredKeyPartyEmails(umr, partyKey) {
      const members = (umr.parties && umr.parties[partyKey]) || []
      const search = (this.recipientSearch || '').toLowerCase().trim()
      if (!search) return members
      return members.filter(m => m.email.toLowerCase().indexOf(search) !== -1)
    },

    // A UMR is "un-sendable" when it can't take any recipient. Per-UMR only:
    // such a UMR shows a support message and no lists, is excluded from the send
    // payload and from the broker-required gate, but does NOT block the OTHER
    // UMRs. Two independent causes:
    //   - brand-new underwriter (not a LimeIQ client), OR
    //   - a MANUAL UMR (is_limeiq false) whose broker is brand-new (not in the
    //     system). A system UMR with no broker contacts (0/0), or a manual UMR
    //     with an existing broker, is NOT un-sendable.
    umrHasNewUnderwriter(umr) {
      return umr && umr.underwriter_limeiq_active === false
    },
    umrHasNewBroker(umr) {
      return umr && umr.is_limeiq === false && umr.broker_limeiq_active === false
    },
    umrUnsendable(umr) {
      return this.umrHasNewUnderwriter(umr) || this.umrHasNewBroker(umr)
    },

    // A UMR party (broker/Underwriter/TPA/coverholder) that had NO contacts on
    // record at load (shows 0/0). Such a party has no vetted mailbox in LimeIQ,
    // so the user may hand-add an any-domain email and it is redirected to
    // support@limeiq.com. Decided from the ORIGINAL (server-loaded) snapshot,
    // NOT the live member list: once the user adds the first any-domain email
    // the live list is >0, but this party is still "no vetted mailbox" and the
    // next "+" must stay blank/editable rather than lock to the added domain.
    umrPartyHasNoContacts(umr, partyKey) {
      if (!umr) return false
      const perParty = this.originalContactCounts[umr.key_id]
      if (perParty && perParty[partyKey] !== undefined) {
        return perParty[partyKey] === 0
      }
      // Fallback (snapshot missing): live count.
      const members = (umr.parties && umr.parties[partyKey]) || []
      return members.length === 0
    },
    // Should THIS party's recipients on THIS UMR be routed to support instead of
    // delivered? A party with no vetted mailbox (0/0 at load) always qualifies.
    // Broker additionally qualifies when flagged a brand-new manual broker, and
    // Underwriter when flagged brand-new (underwriter_limeiq_active === false).
    umrPartyNeedsSupport(umr, partyKey) {
      if (this.umrPartyHasNoContacts(umr, partyKey)) return true
      if (partyKey === 'broker') return this.umrHasNewBroker(umr)
      if (partyKey === 'Underwriter') return this.umrHasNewUnderwriter(umr)
      return false
    },

    // SINGLE-KEY equivalent of umrPartyNeedsSupport: a party group that had NO
    // contacts when the modal loaded has no vetted mailbox in LimeIQ, so a
    // recipient added here is routed to support@limeiq.com on send. Decided from
    // the ORIGINAL snapshot so the notice (and the no-domain-limit rule) stay put
    // after the user adds an address.
    groupNeedsSupport(groupKey) {
      const original = this.originalGroupContactCount[groupKey]
      if (original !== undefined) return original === 0
      return ((this.people[groupKey] || []).length === 0)
    },

    isRecipientSelected(keyId, email) {
      return (this.keyRecipients[keyId] || []).includes(email)
    },

    toggleKeyRecipient(keyId, email, checked) {
      const current = this.keyRecipients[keyId] || []
      if (checked) {
        if (!current.includes(email)) {
          this.$set(this.keyRecipients, keyId, [...current, email])
        }
      } else {
        this.$set(this.keyRecipients, keyId, current.filter(e => e !== email))
      }
    },

    selectedCountByKeyParty(keyId, partyKey) {
      const umr = this.umrGroups.find(g => g.key_id === keyId)
      if (!umr) return 0
      const members = (umr.parties && umr.parties[partyKey]) || []
      const selected = this.keyRecipients[keyId] || []
      return members.filter(m => selected.includes(m.email)).length
    },

    isKeyPartyAllSelected(keyId, partyKey) {
      const umr = this.umrGroups.find(g => g.key_id === keyId)
      if (!umr) return false
      const members = (umr.parties && umr.parties[partyKey]) || []
      const selected = this.keyRecipients[keyId] || []
      return members.length > 0 && members.every(m => selected.includes(m.email))
    },

    isKeyPartyIndeterminate(keyId, partyKey) {
      const umr = this.umrGroups.find(g => g.key_id === keyId)
      if (!umr) return false
      const total = ((umr.parties && umr.parties[partyKey]) || []).length
      const count = this.selectedCountByKeyParty(keyId, partyKey)
      return count > 0 && count < total
    },

    toggleKeyPartyAll(keyId, partyKey, checked) {
      const umr = this.umrGroups.find(g => g.key_id === keyId)
      if (!umr) return
      const members = (umr.parties && umr.parties[partyKey]) || []
      let current = this.keyRecipients[keyId] || []
      if (checked) {
        members.forEach(m => {
          if (!current.includes(m.email)) current = [...current, m.email]
        })
      } else {
        const partyEmails = members.map(m => m.email)
        current = current.filter(e => !partyEmails.includes(e))
      }
      this.$set(this.keyRecipients, keyId, current)
    },

    // Every email selected across all UMRs (deduped) - used for the summary
    // chips and the broker-required check.
    allColeadSelectedEmails() {
      const out = new Set()
      Object.values(this.keyRecipients).forEach(list => {
        (list || []).forEach(e => out.add(e))
      })
      return Array.from(out)
    },

    openAddRecipientColead(umr, party) {
      // Reuse the existing add-recipient popout, but remember which UMR the
      // new email belongs to so it lands in that UMR's selection.
      this.addRecipientGroup = { key: party.key, label: party.label, domainRole: party.domainRole, keyId: umr.key_id }
      this.addRecipientPrefix = ''
      this.addRecipientError = ''

      // Brand-new company (broker or underwriter) on this UMR: NO domain limit -
      // the company isn't in the system so there is no domain to lock to. Leave
      // the domain blank and editable so the user can type the full email. (On
      // send this recipient is redirected to support@limeiq.com.)
      // Any party with no vetted mailbox on this UMR (0/0 at load, or a flagged
      // brand-new broker / underwriter) => NO domain limit; leave the field
      // blank + editable so the user can type any-domain email. On send that
      // recipient is redirected to support@limeiq.com.
      if (this.umrPartyNeedsSupport(umr, party.key)) {
        this.addRecipientDomain = ''
        this.addRecipientDomainLocked = false
        this.addRecipientVisible = true
        return
      }

      // Domain pre-fill for a per-UMR party. Order matters: a co-lead can hold
      // several UMRs with DIFFERENT broker companies, so the GLOBAL configured
      // domain (allowedEmailDomains) is NOT safe to force onto every UMR - it
      // belongs to whichever company happens to be configured, not necessarily
      // THIS UMR's broker. So:
      //   1) infer from THIS UMR's own party members (the correct, UMR-specific
      //      domain), and lock to it,
      //   2) only if this UMR's party has NO members to infer from, fall back to
      //      the configured domain,
      //   3) if there is still nothing (e.g. a 0/0 broker like a system UMR
      //      whose broker company has no contacts on record), leave the domain
      //      BLANK and EDITABLE so the user can type any-domain email. The main
      //      recipient selector remains available for a locked-domain address.
      const members = (umr.parties && umr.parties[party.key]) || []
      let domain = this.inferDomainFromMembers(members)
      let locked = !!domain

      if (!domain) {
        const configured = this.allowedEmailDomains[party.domainRole] || []
        // Only trust the global configured domain when this UMR's party HAS
        // members (they'll share that domain); for a 0/0 party we can't tell
        // which company it is, so stay blank/editable rather than mislead.
        if (members.length && configured.length) {
          domain = configured[0]
          locked = true
        }
      }

      this.addRecipientDomain = domain
      this.addRecipientDomainLocked = locked
      this.addRecipientVisible = true
    },

    // Build the per-UMR send payload from the actual per-key selections.
    // Each entry carries that UMR's chosen recipients, split by role so the
    // backend can render recipients_by_role. Only UMRs with >=1 selection are
    // included; new (non-LimeIQ) underwriters have no selectable recipients so
    // they naturally contribute none.
    buildRecipientsByKey() {
      return this.umrGroups
        .map(umr => {
          const selected = this.keyRecipients[umr.key_id] || []
          if (!selected.length) return null
          const byRole = { broker: [], Underwriter: [], TPA: [], coverholder: [] }
          this.partyConfig.forEach(p => {
            const members = (umr.parties && umr.parties[p.key]) || []
            byRole[p.key] = members
              .map(m => m.email)
              .filter(email => selected.includes(email))
          })
          const bdx = Array.isArray(umr.bdx_key) ? umr.bdx_key[0] : umr.bdx_key
          return {
            key_id: umr.key_id != null ? String(umr.key_id) : (bdx ? 'bdx:' + bdx : 'umr:' + (umr.umr || '')),
            bdx_key: bdx || '',
            umr: umr.umr || '',
            underwriter_lead: umr.underwriter || '',
            recipients: selected,
            recipients_by_role: byRole,
            // Brand-new company on this UMR: the backend must NOT email the
            // broker / underwriter recipient(s) here - it redirects them to
            // support@limeiq.com (in addition to all other recipients) so LimeIQ
            // can create the user and forward the cash call. Kept for backward
            // compatibility with backends that read these two flags.
            redirect_broker_to_support: this.umrPartyNeedsSupport(umr, 'broker'),
            redirect_underwriter_to_support: this.umrPartyNeedsSupport(umr, 'Underwriter'),
            // General form: every party (broker/Underwriter/TPA/coverholder)
            // with no vetted mailbox (0/0 at load, or a flagged brand-new
            // broker/underwriter) whose recipients must route to support rather
            // than be delivered. The backend redirects each listed role's
            // selected emails to support@limeiq.com.
            redirect_roles_to_support: this.partyConfig
              .map(p => p.key)
              .filter(key => this.umrPartyNeedsSupport(umr, key)),
          }
        })
        .filter(Boolean)
    },


    resetFormState() {
      this.recipients = []
      this.activeGroup = null
      this.recipientsExpanded = false
      this.recipientSearch = ''
      this.addRecipientVisible = false
      this.addRecipientGroup = null
      this.addRecipientPrefix = ''
      this.addRecipientDomain = ''
      this.addRecipientDomainLocked = false
      this.addRecipientError = ''
      this.tpaApprovalVisible = false
      this.tpaMembers = []
      this.selectedTpaApprovers = []
      this.tpaMemberSearch = ''
      this.manualApproverEmail = ''
      this.approverDomainError = ''
      this.isCoLead = false
      this.umrGroups = []
      this.keyRecipients = {}
      this.originalContactCounts = {}
      this.originalGroupContactCount = {}
      // Urgency options are per-send choices - clear them so they don't carry
      // over to the next cash call the user sends.
      this.markHighImportance = false
      this.markUrgent = false
    },

    async fetchRecipients() {
      this.loading = true
      try {
        const response = await CashCallService.getRecipients(this.cashCall.id)
        const body = (response.data && response.data.data) || {}
        // The endpoint returns co_lead:true with a per-UMR array, or the flat
        // single-key { broker, Underwriter, TPA, coverholder } shape.
        this.isCoLead = body.co_lead === true
        if (this.isCoLead) {
          this.umrGroups = Array.isArray(body.data) ? body.data : []
          // Fresh per-key selection map, one entry per UMR.
          const map = {}
          // Snapshot each UMR's ORIGINAL per-party contact counts (as loaded
          // from the server, before any hand-added recipients). "Party with no
          // vetted mailbox" must be decided from this snapshot, not the live
          // member list - otherwise, once the user adds the first any-domain
          // email, the party is no longer 0 and the next "+" wrongly locks the
          // domain to that just-added address. Tracked for ALL parties so a 0/0
          // Broker / Underwriter / TPA / Coverholder all behave the same.
          const counts = {}
          this.umrGroups.forEach(g => {
            map[g.key_id] = []
            const perParty = {}
            this.partyConfig.forEach(p => {
              perParty[p.key] = ((g.parties && g.parties[p.key]) || []).length
            })
            counts[g.key_id] = perParty
          })
          this.originalContactCounts = counts
          this.keyRecipients = map
        } else {
          this.people = body.data || {}
          // Snapshot each party's ORIGINAL contact count (before any hand-added
          // recipients) so a 0/0 group keeps showing the support notice.
          const groupCounts = {}
          this.partyConfig.forEach((p) => {
            groupCounts[p.key] = ((this.people[p.key] || []).length)
          })
          this.originalGroupContactCount = groupCounts
          // Pre-select from recipient_emails if available (single-key only).
          if (this.cashCall && this.cashCall.recipient_emails) {
            const existing = this.cashCall.recipient_emails
              .split(',')
              .map(e => e.trim())
              .filter(e => e.length > 0)

            existing.forEach(email => {
              if (!this.recipients.includes(email)) {
                this.recipients.push(email)
              }
            })
          }
        }
      } catch (e) {
        console.error('Error loading recipients:', e)
        this.people = {}
        this.umrGroups = []
        this.keyRecipients = {}
      } finally {
        this.loading = false
      }
    },

    async fetchAllowedEmailDomains() {
      this.loading = true
      try {
        const response = await CashCallService.getAllowedEmailDomains(this.cashCall.id)
        this.allowedEmailDomains = (response.data && response.data.data && response.data.data.data) || {}
      } catch (e) {
        console.error('Error loading allowed email domains:', e)
        this.allowedEmailDomains = {}
      } finally {
        this.loading = false
      }
    },

    isValidEmail1(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    },

    openAddRecipient(group) {
      this.addRecipientGroup = group
      this.addRecipientPrefix = ''
      this.addRecipientError = ''

      // A party with no vetted mailbox (0 contacts at load) gets NO domain
      // limit - the company isn't set up in LimeIQ, so there's no domain to lock
      // to. The typed address is redirected to support on send. Mirrors the
      // co-lead behaviour.
      if (this.groupNeedsSupport(group.key)) {
        this.addRecipientDomain = ''
        this.addRecipientDomainLocked = false
        this.addRecipientVisible = true
        return
      }

      // 1) Prefer the domain configured on the company record.
      const configured = this.allowedEmailDomains[group.domainRole] || []
      let domain = configured.length ? configured[0] : ''
      let locked = configured.length > 0

      // 2) Fallback: some companies have no configured email_domain. In that
      // case infer it from the group's existing member emails (most common
      // domain wins) so the field still pre-fills.
      if (!domain) {
        domain = this.inferGroupDomain(group.key)
        // Lock the field whenever we have a domain (configured or inferred);
        // only leave it editable when no domain could be determined at all.
        locked = !!domain
      }

      this.addRecipientDomain = domain
      this.addRecipientDomainLocked = locked
      this.addRecipientVisible = true
    },

    inferGroupDomain(groupKey) {
      return this.inferDomainFromMembers(this.people[groupKey] || [])
    },

    // Most common email domain across a list of { email } members. Shared by the
    // single-key (flat people) and co-lead (per-UMR parties) add-recipient
    // flows so both pre-fill the domain the same way.
    inferDomainFromMembers(members) {
      const counts = {}
      ;(members || []).forEach(m => {
        const email = (m && m.email) || ''
        const at = email.lastIndexOf('@')
        if (at === -1) return
        const d = email.slice(at + 1).toLowerCase().trim()
        if (d) counts[d] = (counts[d] || 0) + 1
      })
      let best = ''
      let bestCount = 0
      Object.keys(counts).forEach(d => {
        if (counts[d] > bestCount) {
          best = d
          bestCount = counts[d]
        }
      })
      return best
    },

    confirmAddRecipient() {
      this.addRecipientError = ''

      if (!this.canAddRecipient) return

      const prefix = this.addRecipientPrefix.trim()
      const domain = this.addRecipientDomain.trim()
      const email = `${prefix}@${domain}`

      if (!this.isValidEmail1(email)) {
        this.addRecipientError = 'Invalid email format'
        return
      }

      const group = this.addRecipientGroup
      if (group && group.keyId != null) {
        // CO-LEAD: add into that UMR's party list + select under that UMR.
        const umr = this.umrGroups.find(g => g.key_id === group.keyId)
        if (umr) {
          const members = (umr.parties && umr.parties[group.key]) || []
          if (!members.some(m => m.email === email)) {
            members.push({ email, name: prefix, cash_call_approver: null })
            this.$set(umr.parties, group.key, members)
          }
        }
        this.toggleKeyRecipient(group.keyId, email, true)
      } else if (group) {
        // SINGLE-KEY: append to the flat group + select.
        const members = this.people[group.key] || []
        if (!members.some(m => m.email === email)) {
          members.push({ email, name: prefix, cash_call_approver: null })
          this.$set(this.people, group.key, members)
        }
        if (!this.recipients.includes(email)) {
          this.recipients.push(email)
        }
      }

      // Make sure the group it was added to is visible/expanded.
      this.recipientsExpanded = true
      this.addRecipientVisible = false
    },

    removeRecipient(email) {
      this.recipients = this.recipients.filter(e => e !== email)
    },

    closeModal() {
      this.internalVisible = false
      this.resetFormState()
    },

    toggleTpaAll(checked) {
      if (checked) {
        this.filteredTpaMembers.forEach(m => {
          if (!this.selectedTpaApprovers.includes(m.email)) {
            this.selectedTpaApprovers.push(m.email)
          }
        })
      } else {
        const filteredEmails = this.filteredTpaMembers.map(m => m.email)
        this.selectedTpaApprovers = this.selectedTpaApprovers.filter(
          e => !filteredEmails.includes(e)
        )
      }
    },

    addManualApprover() {
      this.approverDomainError = ''

      if (!this.isValidEmail1(this.manualApproverEmail)) {
        this.approverDomainError = 'Invalid email format'
        return
      }

      const domain = this.manualApproverEmail.split('@')[1]

      if (!this.allowedTpaDomains.includes(domain)) {
        this.approverDomainError = 'Email must belong to one of: ' + this.allowedTpaDomains.join(', ')
        return
      }

      if (!this.selectedTpaApprovers.includes(this.manualApproverEmail)) {
        this.selectedTpaApprovers.push(this.manualApproverEmail)
      }

      this.manualApproverEmail = ''
    },

    removeTpaApprover(email) {
      this.selectedTpaApprovers = this.selectedTpaApprovers.filter(e => e !== email)
    },
    async openTpaApprovalModal() {
      this.tpaApprovalVisible = true
      // only show members who are designated cash call approvers
      this.tpaMembers = (this.people['TPA'] || []).filter(
        m => m.cash_call_approver === 'Active'
      )
    },

    async submitTpaApproval() {
      const result = await this.$swal({
        title: 'Are you sure?',
        text: 'This will send the cash call for TPA internal approval.',
        showCancelButton: true,
        confirmButtonText: 'SUBMIT',
        cancelButtonText: 'CANCEL',
        showLoaderOnConfirm: true,
        buttonsStyling: false,
        customClass: {
          popup: 'limeiq-swal',
          confirmButton: 'limeiq-btn',
          cancelButton: 'gray-btn',
        },
        preConfirm: async () => {
          try {
            await this.$emit('submit-tpa-approval', {
              person: this.recipients,
              approvers: this.selectedTpaApprovers,
              cashCallId: this.cashCall.id,
              // Same urgency toggles as the direct Send below - without them the
              // approval (and the market email sent on approval) went out plain.
              highImportance: this.markHighImportance,
              urgent: this.markUrgent,
            })
          } catch (e) {
            this.$swal.showValidationMessage('Failed to send for approval. Please try again.')
            throw e
          }
        },
      })

      if (!result.isConfirmed) return
      this.tpaApprovalVisible = false
      this.closeModal()
    },

    async submitForm() {
      const result = await this.$swal({
        title: 'Are you sure?',
        text: 'Once emailed, the form cannot be modified.',
        showCancelButton: true,
        confirmButtonText: 'SUBMIT',
        cancelButtonText: 'CANCEL',
        showLoaderOnConfirm: true,
        buttonsStyling: false,
        customClass: {
          popup: 'limeiq-swal',
          confirmButton: 'limeiq-btn',
          cancelButton: 'gray-btn',
        },
        preConfirm: async () => {
          try {
            await this.$emit('submit', {
              // Flat deduped list (co-lead: union across UMRs) - kept for
              // backwards compatibility / the combined recipient_email field.
              person: this.isCoLead ? this.allColeadSelectedEmails() : this.recipients,
              cashCallId: this.cashCall.id,
              // Co-lead only: per-UMR recipient selection so each key emails
              // exactly the people picked under it. Built with the party
              // breakdown so the backend/PDF can group by role too.
              recipientsByKey: this.isCoLead ? this.buildRecipientsByKey() : null,
              // Urgency options: high importance flag + URGENT subject (both flows).
              highImportance: this.markHighImportance,
              urgent: this.markUrgent,
            })
          } catch (e) {
            this.$swal.showValidationMessage('Failed to send email. Please try again.')
            throw e
          }
        },
      })

      if (!result.isConfirmed) return
      this.closeModal()
    },
  },
}
</script>

<style>
/* Co-lead: "Broker selection is mandatory" hint beside SELECT RECIPIENTS. */
.broker-required-hint {
  font-size: 0.85rem;
  font-weight: 600;
  color: #6c757d;
  white-space: nowrap;
}
.broker-required-hint--met {
  color: #2e7d32;
}

/* ---- Co-lead per-UMR recipient groups ---- */
.recipient-umr-header {
  background-color: #f1f5f5;
}
.recipient-umr-trigger {
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.recipient-umr-trigger .umr-title {
  font-weight: 700;
  color: #1c4a50;
}
.recipient-umr-trigger .umr-uw {
  color: #525f7f;
}
.umr-new-badge {
  font-size: 0.72rem;
  font-weight: 600;
  color: #4a3b00;
  background-color: #fff6d6;
  border: 1px solid #ffe08a;
  border-radius: 10px;
  padding: 1px 8px;
}
.colead-party-block {
  border: 1px solid #eef1f1;
  border-radius: 6px;
}
.colead-party-header {
  background-color: #fafbfb;
}
.colead-new-uw-msg {
  font-size: 13px;
  color: #4a3b00;
  background-color: #fff6d6;
  border: 1px solid #ffe08a;
  border-radius: 6px;
  padding: 8px 12px;
}
.colead-new-uw-msg a {
  color: #1c4a50;
  font-weight: 600;
  text-decoration: underline;
}

/* Increase width + rounded modal */
.limeiq-swal {
  max-width: none !important;
  border-radius: 14px !important;
  padding: 28px !important;
}

/* Force buttons on same row */
.cash-call-form-submit .swal2-actions {
  display: flex !important;
  flex-direction: row !important;   /*  SAME ROW */
  justify-content: center !important;
  gap: 20px !important;              /* spacing between buttons */
  margin-top: 24px !important;
}

/* Primary button (matches New / Download) */
.limeiq-btn {
  background-color: #1c4a50 !important;
  color: #ffffff !important;
  padding: 12px 28px !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  border: none !important;
  min-width: 180px; 
}

/* Cancel button */
.gray-btn {
  background-color: #E2E8F0 !important;
  color: #333 !important;
  padding: 12px 28px !important;
  border-radius: 8px !important;
  font-weight: 500 !important;
  min-width: 180px;
}

.cash-call-form-submit .swal2-container .swal2-popup {
    max-width: 35.5em !important;
}

.cash-call-form-submit  .swal2-popup {
    max-width: 35.5em !important;
}

/* SWEETALERT2 CUSTOM THEME - LIMEIQ STYLE */

.cash-call-form-submit .swal2-actions {
  gap: 18px !important;
  margin-top: 20px !important;
}

.cash-call-form-submit .swal2-popup {
  border-radius: 12px !important;
  padding: 1.75rem !important;
  box-shadow: 0px 4px 20px rgba(0,0,0,0.15) !important;
}

.cash-call-form-submit .swal2-title {
  font-size: 1.45rem !important;
  font-weight: 600 !important;
  color: #1e2a35 !important;
}

.cash-call-form-submit .swal2-html-container {
  font-size: 1rem !important;
  color: #4a5568 !important;
  margin-top: 8px !important;
}

.cash-call-form-submit .swal2-confirm {
  background-color: #1c4a50 !important;  /* <-- LimeIQ teal/dark green */
  color: white !important;
  border-radius: 8px !important;
  padding: 10px 26px !important;
  font-weight: 400 !important;
  border: none !important;
  box-shadow: none !important;
}

.cash-call-form-submit .swal2-cancel {
  background-color: #E2E8F0 !important;
  color: #333 !important;
  border-radius: 8px !important;
  padding: 10px 26px !important;
  font-weight: 500 !important;
}

.cash-call-form-submit .swal2-confirm:hover {
   background-color: #0B5345 !important; 
}

.cash-call-form-submit .swal2-cancel:hover {
  background-color: #CBD5E1 !important;
}

.cash-call-form-submit .swal2-icon-warning {
  border-color: #F6AD55 !important;
  color: #DD6B20 !important;
}

.cash-call-form-submit .swal2-icon-success {
  border-color: #68D391 !important;
  color: #38A169 !important;
}

.cash-call-form-submit .swal2-icon-error {
  border-color: #FEB2B2 !important;
  color: #E53E3E !important;
}

/* SPACING BETWEEN SWEETALERT BUTTONS */
.cash-call-form-submit .swal2-actions {
  gap: 18px !important;    /* Add space between DELETE and CANCEL */
  margin-top: 20px !important;
}

/* LIMEIQ PRIMARY BUTTON (MATCHES NEW / DOWNLOAD BUTTON COLOR) */
.cash-call-form-submit .swal2-confirm.limeiq-btn {
  background-color: #1c4a50 !important;  /* <-- LimeIQ teal/dark green */
  color: white !important;
  border-radius: 8px !important;
  padding: 10px 26px !important;
  font-weight: 600 !important;
  border: none !important;
  box-shadow: none !important;
}

.cash-call-form-submit .swal2-confirm.limeiq-btn:hover {
  background-color: #0B5345 !important;  /* darker hover */
}

/* CANCEL BUTTON */
.cash-call-form-submit .swal2-cancel.gray-btn {
  background-color: #E2E8F0 !important;
  color: #333 !important;
  border-radius: 8px !important;
  padding: 10px 26px !important;
  font-weight: 500 !important;
}

.cash-call-form-submit .swal2-cancel.gray-btn:hover {
  background-color: #CBD5E1 !important;
}

.v-select {
  position: relative;
  font-size: 14px;
}

.v-select .vs__dropdown-toggle {
  display: flex;
  align-items: center;
  padding: 6px 10px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: #fff;
  min-height: 38px;
}

.v-select .vs__selected {
  margin: 2px 4px;
  background: #e6f2f2;
  border-radius: 4px;
  padding: 2px 6px;
}

.v-select .vs__dropdown-menu {
  position: absolute;
  z-index: 1000;
  width: 70%;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  max-height: 200px;
  overflow-y: auto;
}

.v-select .vs__search {
  border: none;
  outline: none;
  flex: 1;
}


</style>


<style scoped>
/* Urgency toggles at the bottom of the recipient stage (co-lead). */
.cc-send-urgency {
  clear: both;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #eef1f3;
}
.cc-send-urgency__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0;
}
.cc-send-urgency__label {
  font-weight: 600;
  color: #32325d;
}

.recipient-accordion-header {
  background: #f8f9fa !important;
  border: none;
}

.recipient-accordion-trigger {
  cursor: pointer;
  user-select: none;
}

.recipient-accordion-trigger:hover {
  background: #f0f0f0;
}

.group-title {
  font-weight: 600;
  color: #1c4a50;
  font-size: 14px;
  text-transform: capitalize;
}

.selected-count {
  font-size: 12px;
}

.chevron {
  border: solid #1c4a50;
  border-width: 0 2px 2px 0;
  padding: 4px;
  display: inline-block;
  transform: rotate(45deg);
  transition: transform 0.25s ease;
}

.chevron.rotated {
  transform: rotate(-135deg);
}

.badge-info {
  background-color: lightgoldenrodyellow !important;
  color: #333 !important;
}

.cursor-pointer {
  cursor: pointer;
}
.card .card-header{
  min-height: initial !important;
}

/* ---- Mock-matched recipient selector ---- */
.recipients-label {
  display: block;
  font-family: 'Segoe UI Regular', sans-serif;
  font-weight: bold;
  color: teal;
  margin-bottom: 6px;
}

.select-recipients-toggle {
  font-weight: 700;
  letter-spacing: 0.3px;
  background: transparent;
  color: #1c4a50;
  border: 1px solid #1c4a50;
  border-radius: 6px;
  padding: 8px 16px;
}

.select-recipients-toggle:hover,
.select-recipients-toggle:focus {
  background: #1c4a50;
  color: #fff;
}

.toggle-caret {
  font-size: 11px;
}

.recipient-global-search {
  border-radius: 6px;
}

.count-badge {
  font-size: 12px;
  color: #6c757d;
}

.group-toggle-area {
  cursor: pointer;
}

/* group select-all checkbox sits inline in the header */
.group-select-all {
  width: 16px;
  height: 16px;
  cursor: pointer;
  flex: 0 0 auto;
  accent-color: #1c4a50;
}

.add-new-btn {
  width: 26px;
  height: 26px;
  padding: 0;
  line-height: 1;
  font-size: 16px;
  font-weight: 700;
  border-radius: 50%;
  background: transparent;
  color: #1c4a50;
  border: 1px solid #1c4a50;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.add-new-btn:hover,
.add-new-btn:focus {
  background: #1c4a50;
  color: #fff;
}

/* ---- Add new recipient popout ---- */
.add-recipient-field-label {
  font-size: 13px;
  color: #4a5568;
  margin-bottom: 4px;
  display: block;
}

.add-recipient-input-row {
  align-items: stretch;
}

.add-recipient-prefix {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.add-recipient-at {
  display: inline-flex;
  align-items: center;
  padding: 0 8px;
  background: #f1f3f5;
  border-top: 1px solid #ced4da;
  border-bottom: 1px solid #ced4da;
  color: #6c757d;
}

.add-recipient-domain {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.add-recipient-domain.is-locked {
  background: #e9ecef;
  color: #6c757d;
  cursor: not-allowed;
}

.add-recipient-helper {
  font-size: 12px;
  color: #6c757d;
  margin-top: 8px;
  margin-bottom: 0;
}
</style>